/**
 * Store Import Table Component
 * 
 * Editable table for reviewing and editing stores before import.
 * 
 * @module StoreImportTable
 */

import React from 'react';
import styled from 'styled-components';
import { Edit2, Trash2, Plus, X, Check } from 'lucide-react';
import { StoreImportRow } from '../../services/StoreImportService';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';

/**
 * Table Container
 */
const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid ${ThemeColors.border.light};
  overflow: hidden;
  margin-bottom: ${Spacing.lg};
`;

/**
 * Table
 */
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${ThemeColors.background.secondary};
  border-bottom: 2px solid ${ThemeColors.border.main};
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: ${Spacing.md};
  text-align: left;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ isInvalid?: boolean; isEditing?: boolean }>`
  border-bottom: 1px solid ${ThemeColors.border.light};
  background: ${props => {
    if (props.isEditing) return ThemeColors.primary.light;
    if (props.isInvalid) return ThemeColors.error.light;
    return 'white';
  }};

  &:hover {
    background: ${props => {
      if (props.isEditing) return ThemeColors.primary.light;
      if (props.isInvalid) return ThemeColors.error.light;
      return ThemeColors.background.secondary;
    }};
  }
`;

const TableCell = styled.td`
  padding: ${Spacing.sm} ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
`;

const EditableInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${props => props.hasError ? ThemeColors.error.main : ThemeColors.border.main};
  border-radius: 4px;
  font-size: ${Typography.fontSize.sm};
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? ThemeColors.error.main : ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${props => props.hasError ? ThemeColors.error.light : ThemeColors.primary.light};
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${Spacing.xs};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${ThemeColors.text.secondary};
  transition: all 0.2s ease;
  margin-right: ${Spacing.xs};

  &:hover {
    color: ${ThemeColors.primary.main};
    transform: scale(1.1);
  }

  &.danger:hover {
    color: ${ThemeColors.error.main};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  background: ${ThemeColors.error.main};
  color: white;
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  margin-left: ${Spacing.xs};
`;

const EmptyState = styled.div`
  padding: ${Spacing.xl};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: ${Spacing.md};

  &:hover {
    background: ${ThemeColors.primary.dark};
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

/**
 * Store Import Table Props
 */
export interface StoreImportTableProps {
  stores: StoreImportRow[];
  onUpdate: (id: string, field: keyof StoreImportRow, value: string) => void;
  onRemove: (id: string) => void;
  onAdd: () => void;
  editingId?: string;
  onEditStart: (id: string) => void;
  onEditCancel: () => void;
  onEditSave: (id: string) => void;
}

/**
 * Store Import Table Component
 */
export const StoreImportTable: React.FC<StoreImportTableProps> = ({
  stores,
  onUpdate,
  onRemove,
  onAdd,
  editingId,
  onEditStart,
  onEditCancel,
  onEditSave
}) => {
  const getFieldValue = (store: StoreImportRow, field: keyof StoreImportRow): string => {
    const value = store[field];
    return value ? String(value) : '';
  };

  const handleCellChange = (id: string, field: keyof StoreImportRow, value: string) => {
    onUpdate(id, field, value);
  };

  if (stores.length === 0) {
    return (
      <EmptyState>
        <p>No stores to display. Upload a CSV file or add a store manually.</p>
      </EmptyState>
    );
  }

  return (
    <div>
      <AddButton onClick={onAdd}>
        <Plus />
        Add Store
      </AddButton>

      <TableContainer>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>Customer No</TableHeaderCell>
              <TableHeaderCell>Store Name</TableHeaderCell>
              <TableHeaderCell>Address</TableHeaderCell>
              <TableHeaderCell>City</TableHeaderCell>
              <TableHeaderCell>Prov</TableHeaderCell>
              <TableHeaderCell>Postal Code</TableHeaderCell>
              <TableHeaderCell>Store Number</TableHeaderCell>
              <TableHeaderCell>Sort Field</TableHeaderCell>
              <TableHeaderCell>Telephone</TableHeaderCell>
              <TableHeaderCell>Sales Group</TableHeaderCell>
              <TableHeaderCell>Salesperson No</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {stores.map((store) => {
              const isEditing = editingId === store.id;
              const isInvalid = !store.isValid;

              return (
                <TableRow key={store.id} isInvalid={isInvalid} isEditing={isEditing}>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'CustomerNo')}
                        onChange={(e) => handleCellChange(store.id!, 'CustomerNo', e.target.value)}
                        hasError={!store.CustomerNo?.trim()}
                      />
                    ) : (
                      <>
                        {store.CustomerNo || '-'}
                        {!store.CustomerNo?.trim() && <ErrorBadge>Required</ErrorBadge>}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'StoreName')}
                        onChange={(e) => handleCellChange(store.id!, 'StoreName', e.target.value)}
                        hasError={!store.StoreName?.trim()}
                      />
                    ) : (
                      <>
                        {store.StoreName || '-'}
                        {!store.StoreName?.trim() && <ErrorBadge>Required</ErrorBadge>}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'Address')}
                        onChange={(e) => handleCellChange(store.id!, 'Address', e.target.value)}
                        hasError={!store.Address?.trim()}
                      />
                    ) : (
                      <>
                        {store.Address || '-'}
                        {!store.Address?.trim() && <ErrorBadge>Required</ErrorBadge>}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'City')}
                        onChange={(e) => handleCellChange(store.id!, 'City', e.target.value)}
                        hasError={!store.City?.trim()}
                      />
                    ) : (
                      <>
                        {store.City || '-'}
                        {!store.City?.trim() && <ErrorBadge>Required</ErrorBadge>}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'Prov')}
                        onChange={(e) => handleCellChange(store.id!, 'Prov', e.target.value)}
                      />
                    ) : (
                      store.Prov || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'PostalCode')}
                        onChange={(e) => handleCellChange(store.id!, 'PostalCode', e.target.value)}
                      />
                    ) : (
                      store.PostalCode || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'StoreNumber')}
                        onChange={(e) => handleCellChange(store.id!, 'StoreNumber', e.target.value)}
                      />
                    ) : (
                      store.StoreNumber || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'SortField')}
                        onChange={(e) => handleCellChange(store.id!, 'SortField', e.target.value)}
                      />
                    ) : (
                      store.SortField || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'Telephone')}
                        onChange={(e) => handleCellChange(store.id!, 'Telephone', e.target.value)}
                      />
                    ) : (
                      store.Telephone || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'SalesGroup')}
                        onChange={(e) => handleCellChange(store.id!, 'SalesGroup', e.target.value)}
                      />
                    ) : (
                      store.SalesGroup || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <EditableInput
                        value={getFieldValue(store, 'SalespersonNo')}
                        onChange={(e) => handleCellChange(store.id!, 'SalespersonNo', e.target.value)}
                      />
                    ) : (
                      store.SalespersonNo || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <>
                        <ActionButton
                          onClick={() => onEditSave(store.id!)}
                          title="Save"
                        >
                          <Check />
                        </ActionButton>
                        <ActionButton
                          onClick={onEditCancel}
                          title="Cancel"
                        >
                          <X />
                        </ActionButton>
                      </>
                    ) : (
                      <>
                        <ActionButton
                          onClick={() => onEditStart(store.id!)}
                          title="Edit"
                        >
                          <Edit2 />
                        </ActionButton>
                        <ActionButton
                          onClick={() => onRemove(store.id!)}
                          title="Remove"
                          className="danger"
                        >
                          <Trash2 />
                        </ActionButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

